import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo, { KiteMark } from "@/components/Logo";
import { scrollToId } from "@/lib/api";

const LINKS = [
  { label: "About", href: "#about" },
  { label: "Vision", href: "#vision" },
  { label: "Gallery", href: "#gallery" },
  { label: "Events", href: "/events", route: true },
  { label: "Reels", href: "#reels" },
  { label: "Blog", href: "#blog" },
  { label: "Contact", href: "#contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(href);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-300 ${
        scrolled ? "bg-paper/90 backdrop-blur-md shadow-[0_1px_0_rgba(20,53,92,0.15)]" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="max-w-7xl mx-auto flex items-center justify-between px-5 sm:px-8 py-3">
        <a
          href="#home"
          onClick={(e) => go(e, "#home")}
          data-testid="nav-home-link"
          aria-label="Wind Skaters home"
          className="shrink-0"
        >
          <Logo />
        </a>

        <div className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) =>
            l.route ? (
              <Link
                key={l.href}
                to={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="font-body font-semibold text-navy/80 hover:text-verm transition-colors duration-300 text-[15px]"
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => go(e, l.href)}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="font-body font-semibold text-navy/80 hover:text-verm transition-colors duration-300 text-[15px]"
              >
                {l.label}
              </a>
            )
          )}
          <a
            href="#contact"
            onClick={(e) => go(e, "#contact")}
            data-testid="nav-cta-fly-with-us"
            className="group inline-flex items-center gap-2 bg-navy text-paper font-semibold px-6 py-2.5 rounded-full border-2 border-marigold hover:bg-verm transition-colors duration-300"
          >
            Fly With Us
            <KiteMark className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300" />
          </a>
        </div>

        <button
          className="lg:hidden text-navy p-2"
          onClick={() => setOpen((o) => !o)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden bg-paper/95 backdrop-blur-md border-t border-navy/10 overflow-hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-4 flex flex-col gap-4">
              {LINKS.map((l) =>
                l.route ? (
                  <Link
                    key={l.href}
                    to={l.href}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                    className="font-body font-semibold text-navy text-lg"
                  >
                    {l.label}
                  </Link>
                ) : (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={(e) => go(e, l.href)}
                    className="font-body font-semibold text-navy text-lg"
                  >
                    {l.label}
                  </a>
                )
              )}
              <a
                href="#contact"
                onClick={(e) => go(e, "#contact")}
                className="inline-flex justify-center items-center gap-2 bg-navy text-paper font-semibold px-6 py-3 rounded-full border-2 border-marigold"
              >
                Fly With Us
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
